"use client";
import {
  ButtonComponent,
  DividerComponent,
  LoaderComponent,
  ModalComponent,
  PaperComponent,
  SnackbarComponent,
  TextFieldComponent,
} from "@/components";
import { useRouter } from "@/i18n/routing";
import {
  APP_KEYS,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  MANDATE_SETUP_NOTIFICATION,
  NUMBER_REGEX,
  PAYMENT_METHODS_MAPPING,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  SUBSTAGE,
  WEB_COLORS,
  WEB_EVENTS,
  resCodeErrorValidator,
} from "@/lib/constants";
import { useFooter } from "@/lib/context/FooterContext";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import useLogoutHook from "@/lib/hooks/logoutHook";
import useStageErrorHandler from "@/lib/hooks/stageErrorHandler";
import {
  useBankMandateMutation,
  useGetApplicationQuery,
  useGetApplicationStageConfigQuery,
  useGetCustomerQuery,
  useUpiMandateMutation,
  useUpiMandateStatusMutation,
} from "@/lib/redux";
import { Info } from "@mui/icons-material";
import {
  Box,
  FormControl,
  IconButton,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormik } from "formik";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as Yup from "yup";
import ErrorModal from "./error-modal";
import PaymentMethods from "./payment-methods";
import ENV from "@/lib/constants/EnvVariables";
import ROUTES from "@/lib/constants/PageRoutes";
import { useDispatch } from "react-redux";
import { setModalAttributes } from "@/lib/redux/slice/ModalSlice";
import { checkForRpl, checkForRplProductRoute } from "@/lib/utils/rpl.utils";
import CurrentAccountWarningModal from "@/screens/(offer)/offers/CurrentAccountWarningModal";
import { useSearchParams } from "next/navigation";
import { setCurrentAccountWarningShown } from "@/lib/redux/slice/ApplicationSlice";
import { sessionStorageUtil } from "@/lib/utils/common.utils";
import { usePolling } from "@/lib/hooks/common.hooks";


// mandate status api pooling count
const MAX_MANDATE_COUNT = 2;

const MANDATE_RETRY_COUNT_LIMIT = ENV.MANDATE_RETRY_COUNT_LIMIT;

const RepaymentModeScreen = ({ product }) => {
  const t = useTranslations("repaymentSetUp");
  const params = useSearchParams();
  const router = useRouter();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { setActions } = useFooter();
  const dispatch = useDispatch();
  const [count, setCount] = useState(0);
  const applicationRef = useRef();
  const [loader, setLoader] = useState(false);
  const [currentAccountModal, setCurrentAccountModal] = useState(false);
  const [openAccountModal, setOpenAccountModal] = useState(false);
  const [errorModalDetails, setErrorModalDetails] = useState({
    label: "",
    subLabel: "",
    modalErrorType: "",
    buttonLabel: "Retry",
  });
  const { handleLogout } = useLogoutHook();
  const [modalVisible, setModalVisible] = useState(false);
  const [openUpiModal, setOpenUpiModal] = useState(false);
  const [openUpiInput, setOpenUpiInput] = useState(false);
  const upiTimerRef = useRef(null);
  const m = useTranslations("ErrorModal.mandateLimitExhausted");

  const mandateStatus = params?.get("mandate_status");
  const orderStatus = params?.get("status");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");

  /***************************************RTK************************************ */
  // fetch application data from get api
  const {
    data: applicationData,
    error: errorGetApplication,
    isLoading: applicationLoading,
    refetch: refetchApplicationData,
    isSuccess: isSuccessApplication,
  } = useGetApplicationQuery(null, {
    refetchOnMountOrArgChange: true,
  });
  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data,
  );

  const isRPL = checkForRpl(productApplication);

  const {
    data: appStageConfigData,
    isLoading: isLoadingAppStageConfig,
    error: appStageConfigError,
  } = useGetApplicationStageConfigQuery(
    {
      appId: productApplication?.appId,
      stage: productApplication?.stage,
    },
    {
      skip: !productApplication?.appId || !productApplication?.stage,
      refetchOnMountOrArgChange: true,
    },
  );

  const mandateExhaustedObj = useMemo(() => {
    const eCount = productApplication?.eMandateRetryCount;
    const upiCount = productApplication?.upiMandateRetryCount;
    const stage = productApplication?.stage;
    const subStage = productApplication?.subStage;

    const allExhausted =
      stage === STAGE.MANDATE &&
      ((eCount === ENV.MAX_E_MANDATE_RETRY_COUNT &&
        upiCount === ENV.MAX_UPI_MANDATE_RETRY_COUNT) ||
        subStage === SUBSTAGE.MANDATE_LIMIT_EXHAUSTED);

    return {
      open: allExhausted,
      routeToHome: allExhausted,
    };
  }, [productApplication]);

  applicationRef.current = productApplication;

  const { data: customerData, isLoading: fetchingCustomer } =
    useGetCustomerQuery();
  const [
    upiMandate,
    {
      data: upiMandateData,
      error: errorUpiMandate,
      isError: isErrorUpiMandate,
      isLoading: upiMandateLoading,
      reset: resetUpiMandate,
    },
  ] = useUpiMandateMutation();

  const [
    upiMandateStatus,
    {
      data: upiMandateStatusData,
      error: errorUpiMandateStatus,
      isLoading: upiMandateStatusLoading,
      reset: resetUpiMandateStatus,
    },
  ] = useUpiMandateStatusMutation();
  const [
    createMandate,
    {
      data: createMandateData,
      error: errorCreateMandate,
      isLoading: createMandateLoading,
    },
  ] = useBankMandateMutation();

  const clearUpiTimer = () => {
    if (upiTimerRef.current) {
      clearTimeout(upiTimerRef.current);
      upiTimerRef.current = null;
    }
  };

  const handleMandateRetryCheck = (data) => {
    //later remove this for create mandate api is live
    return;
    !modalVisible && setModalVisible(true);
    const hasExhausted = MANDATE_RETRY_COUNT_LIMIT <= data?.mandateRetryCount;
    setErrorModalDetails((d) => {
      let prev = { ...d };
      //if label isnot there

      if (d.subLabel) {
        prev.error = data?.mandateRetryCountMessage;
      }
      if (hasExhausted) {
        if (!d.label) {
          prev.label = m("label");
        }
        prev.onBtnClick = function () {
          setModalVisible(false);
          router.push("/");
        };
      }
      return prev;
    });
  };

  const handleApplicationPollingStopCondition = useCallback(
    (response) => {
      let shouldStop = false;
      const application = response?.data?.applications?.find(
        (app) => app.product_type === PRODUCT_HANDLER[product],
      );
      const rplApplication = response?.data?.applications?.find(
        (app) => app.sub_product_type === PRODUCT_HANDLER[product],
      );
      if (rplApplication) {
        const rplSubStage = rplApplication?.subStage;
        shouldStop =
          rplSubStage != null && rplSubStage !== SUBSTAGE.MANDATE_POLLING;
      } else {
        const subStage = application?.subStage;
        shouldStop = subStage != null && subStage !== SUBSTAGE.MANDATE_POLLING;
      }
      return shouldStop;
    },
    [product],
  );
  const {
    data: polledApplicationData,
    isPolling: isPollingApplication,
    isFetching: isFetchingApplication,
    resetPolling: resetPollingApplication,
    startPolling: startPollingApplication,
  } = usePolling({
    pollFn: async () => {
      const result = await refetchApplicationData();
      return result;
    },
    interval: 5000,
    stopCondition: handleApplicationPollingStopCondition,
    onComplete: (data) => {
      if (
        !mandateStatus &&
        orderStatus?.toLowerCase() === "charged" &&
        ["RPD_SUCCESS"].includes(productApplication?.subStage)
      ) {
        upiMandate({
          appId: productApplication?.appId,
        });
      }

      if (mandateStatus?.toLowerCase() === "failure") {
        setErrorModalDetails({
          modalErrorType: "emandateFailed",
          onBtnClick: () => setModalVisible(false),
        });
        handleMandateRetryCheck(mandateStatusData?.data?.data);
        setModalVisible(true);
      }
    },
    onStart: () => {
      console.log("Polling started for application data");
    },
  });

  const isError = errorGetApplication || errorCreateMandate || errorUpiMandate;
  const isloading =
    applicationLoading ||
    upiMandateLoading ||
    upiMandateStatusLoading ||
    fetchingCustomer ||
    createMandateLoading ||
    isPollingApplication ||
    isFetchingApplication;

  const formik = useFormik({
    initialValues: { modeOfPayment: "", upiId: "" },
    validationSchema: Yup.object().shape({
      modeOfPayment: Yup.string().required("Mode of Payment is Required"),
      upiId: Yup.string().when("modeOfPayment", {
        is: (value) => openUpiInput && value === "upi",
        then: () =>
          Yup.string()
            .trim()
            .required(
              "Entered UPI Id is invalid. Please try with a correct format as shown below.",
            )
            .max(45, "UPI ID must not exceed 45 characters")
            .test(
              "upi-format",
              "Entered UPI Id is invalid. Please try with a correct format as shown below.",
              (value) => {
                if (!value) return false;
                if (!value.includes("@")) return false;
                if (value.endsWith("@")) return true;
                return /^[a-zA-Z0-9._-]+@[a-zA-Z0-9]+$/.test(value);
              },
            ),
        otherwise: () => Yup.string().notRequired(),
      }),
    }),
    onSubmit: (values) => {
      sendEventAnalytics({
        eventName: isRPL
          ? WEB_EVENTS.RL_ON_MANDATE
          : WEB_EVENTS.MODE_OF_REPAYMENT_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
      if (values?.modeOfPayment === "upi") {
        sendEventAnalytics({
          eventName: isRPL
            ? WEB_EVENTS.RL_UPI_AUTO_PAY_STARTED
            : WEB_EVENTS.UPI_AUTO_PAY_STARTED,
          ...productApplication,
          ...customerData?.data,
        });
        setLoader(true);
        // reset the count and mandate api store before making mandate call
        resetUpiMandateStatus();
        resetUpiMandate();
        setCount(0);
        clearUpiTimer();

        // upiMandate({
        //   appId: productApplication?.appId,
        //   customerVpa: values?.upiId,
        // });
        if (productApplication?.bankVerificationMode?.toLowerCase() !== "aa") {
          upiMandate({
            appId: productApplication?.appId,
            customerVpa: values?.upiId,
          });
        } else if (
          productApplication?.bankVerificationMode?.toLowerCase() === "aa" &&
          !productApplication?.bankAccountNumber.includes("X")
        ) {
          upiMandate({
            appId: productApplication?.appId,
            accountNumber: productApplication?.bankAccountNumber,
            customerVpa: values?.upiId,
          });
        } else {
          setOpenAccountModal(true);
        }
      } else {
        // for Debit card / Netbanking
        if (productApplication?.bankVerificationMode === "PENNYDROP") {
          // pennydrop in BS journey
          if (isRPL) {
            // handleRplEMandateCall(values?.modeOfPayment);
            sendEventAnalytics({
              eventName:
                values?.modeOfPayment === "debitCard"
                  ? WEB_EVENTS.RL_DEBIT_CARD_STARTED
                  : WEB_EVENTS.RL_NETBANKING_STARTED,
              ...productApplication,
              ...customerData?.data,
            });
            createMandate({
              appId: productApplication?.appId,
              paymentMethodType: PAYMENT_METHODS_MAPPING[values?.modeOfPayment],
            });
            return;
          } else {
            sendEventAnalytics({
              eventName:
                values.modeOfPayment === "debitCard"
                  ? WEB_EVENTS.MODE_OF_REPAYMENT_DEBIT_CARD_STARTED
                  : WEB_EVENTS.MODE_OF_REPAYMENT_NETBANKING_STARTED,
              ...productApplication,
              ...customerData?.data,
            });

            router.push(
              `/${product}/mandate/repayment-banking?mode=${values.modeOfPayment}`,
            );
            return;
          }
        } else {
          if (isRPL) return; // double validation  -> RPL doesnot has  aa/perfios journey

          // aa and perfios journey
          const {
            bankVerificationMode,
            aaCallBackIfscCode,
            perfiosCallBackIfscCode,
            aaCallBackAccountNumber,
            perfiosCallBackAccountNumber,
          } = productApplication;

          const ifscByMode = {
            AA: aaCallBackIfscCode,
            PERFIOS: perfiosCallBackIfscCode,
          };

          const accByMode = {
            AA: aaCallBackAccountNumber,
            PERFIOS: perfiosCallBackAccountNumber,
          };

          const mode = bankVerificationMode;

          if (mode === "AA" || mode === "PERFIOS") {
            router.push(
              `/personal-loan/mandate/repayment-banking?mode=${mode}`,
            );
            return;
          }
          // perfios & aa flow
          createMandate({
            appId: productApplication?.appId,
            paymentMethodType: PAYMENT_METHODS_MAPPING[values?.modeOfPayment],
          });
        }
      }
    },
  });
  const handleCurrentAccountWarningSuccess = useCallback(() => {
    setCurrentAccountModal(false);
    dispatch(setCurrentAccountWarningShown(true));
    sessionStorageUtil.set(APP_KEYS.CURRENT_ACCOUNT_WARNING_SHOWN, 1);
  }, [dispatch, setCurrentAccountModal]);

  /***************************************EFFECT************************************ */
  useErrorHandling(isError);
  useEffect(() => {
    const isRPL = checkForRplProductRoute(product);
    sendEventAnalytics({
      eventName: isRPL
        ? WEB_EVENTS.RL_ON_MANDATE
        : WEB_EVENTS.MODE_OF_REPAYMENT_STARTED,
      ...productApplication,
      ...customerData?.data,
    });
  }, []);
  useEffect(() => {
    const subStage = productApplication?.subStage;
    const stage = productApplication?.stage;
    const hasMandateAwaitingSubStage =
      appStageConfigData?.data?.subStageHold?.includes(subStage);

    if (isRPL && stage === STAGE.MANDATE && hasMandateAwaitingSubStage) {
      // for RPL, for certain substages on mandate Stage needs to be rerouted to penny drop
      router.push(STAGE_BASED_ROUTING(STAGE.MANDATE_PENNYDROP));
      return;
    }
    if (
      productApplication?.stage &&
      productApplication?.stage !== STAGE.MANDATE
    ) {
      if (
        productApplication?.stage === STAGE.E_SIGN &&
        upiMandateData?.data?.mandateId
      ) {
        // to esign instructions if only valid UPImandateId exists
        router.push(ROUTES(product).E_SIGN_INSTRUCTIONS);
        return;
      }
      const route = STAGE_BASED_ROUTING(productApplication?.stage);
      router.push(route);
      return;
    }

    if (subStage === SUBSTAGE.PENNYDROP_RETRY) {
      setCurrentAccountModal(true);
    }
    if (subStage === SUBSTAGE.MANDATE_POLLING) {
      startPollingApplication();
      return;
    }
    // exhausted all mandate attempts
    if (mandateExhaustedObj?.open) {
      dispatch(
        setModalAttributes({
          open: true,
          message: {
            label: m("label"),
            subLabel: m("subLabel"),
          },
          type: "ATTEMPT_ERROR",
        }),
      );
      // setErrorModalDetails({
      //   label: m("label"),
      //   subLabel: m("subLabel"),
      //   buttonLabel: "Ok",
      //   onBtnClick: function () {
      //     setModalVisible(false);
      //     mandateExhaustedObj?.routeToHome && router.push("/");
      //   },
      //   hideCloseBtn: true,
      // });
      // setModalVisible(true);
    }
  }, [
    isRPL,
    productApplication,
    appStageConfigData,
    upiMandateData,
    setModalVisible,
    setErrorModalDetails,
    mandateExhaustedObj,
  ]);

  useEffect(() => {
    if (!isSuccessApplication) {
      refetchApplicationData();
    }
  }, [applicationData]);

  useEffect(() => {
    if (errorUpiMandate) {
      const { status } = errorUpiMandate;
      if (status === 401) {
        setLoader(false);
        SnackbarComponent({
          error: true,
          errorMessage: "Your session has expired. Please login again",
        });
        handleLogout(); //navigate to login screen
      }
      if (errorUpiMandate?.data?.detail?.code === "MN001") {
        setOpenUpiInput(true);
      }
      handleMandateRetryCheck(errorUpiMandate?.data?.data);
      setLoader(false);
      return;
    }
    if (upiMandateData?.data?.paymentLink) {
      window.location.href = upiMandateData?.data?.paymentLink;
      return;
    }
    if (upiMandateData?.data) {
      if (
        upiMandateData?.data?.reason ===
        SUBSTAGE.UCIC_HUNTER_DECISION_POSTPONED_REJECT
      ) {
        refetchApplicationData();
        return;
      }
      if (
        upiMandateData?.data?.responseCode === "UPIRes002" &&
        upiMandateData?.data?.mandateId
      ) {
        setLoader(true);
        setOpenUpiModal(true);
        let requestedData = {
          appId: productApplication?.appId,
          mandateId: upiMandateData?.data?.mandateId,
        };
        clearUpiTimer();

        upiTimerRef.current = setTimeout(() => {
          if (errorUpiMandate) return;
          setCount((prev) => prev + 1);
          upiMandateStatus(requestedData);
        }, 60000);

        return () => clearUpiTimer();
      } else if (upiMandateData?.data?.responseCode !== "UPIRes002") {
        setLoader(false);
        setErrorModalDetails(false);
        setErrorModalDetails({
          // error: upiMandateData?.data?.mandateCountErrorMessage,
          label: upiMandateData?.message,
          subLabel: upiMandateData?.data?.reason,
        });
        handleMandateRetryCheck(upiMandateData?.data?.data);
        setModalVisible(true);
      }
    }
  }, [upiMandateData, errorUpiMandate, isErrorUpiMandate]);

  // handle upi mandate status response and errors
  useEffect(() => {
    const scheduleUpiMandateStatus = () => {
      let requestedData = {
        appId: productApplication?.appId,
        mandateId: upiMandateData?.data?.mandateId,
      };
      clearUpiTimer();
      upiTimerRef.current = setTimeout(() => {
        setCount((prev) => prev + 1);
        upiMandateStatus(requestedData);
      }, 60000);

      return () => clearUpiTimer();
    };

    const showErrorModal = (err) => {
      const errObj = {};
      if (err?.data) {
        errObj.label = err?.data?.message;
        errObj.subLabel = err?.data?.reason || err?.data?.message;
        if (errObj.label === errObj.subLabel) {
          delete errObj.label;
        }
      } else {
        errObj.subLabel = err?.message;
      }
      // close polling and show error
      setLoader(false);
      setOpenUpiModal(false);
      setErrorModalDetails(errObj);
      setModalVisible(true);
      return;
    };
    const errorUpiMandateStatusData = errorUpiMandateStatus?.data;
    if (errorUpiMandateStatusData) {
      const errStatusCode = errorUpiMandateStatusData?.statusCode;
      if (errorUpiMandateStatusData?.status === 401) {
        SnackbarComponent({
          error: true,
          errorMessage: "Your session has expired. Please login again",
        });
        handleLogout(); //navigate to login screen
        return;
      } else if (
        errStatusCode === 404 &&
        errorUpiMandateStatusData?.data?.responseCode === "UPIRES-000"
      ) {
        showErrorModal(errorUpiMandateStatusData);
        return;
      } else if (
        errStatusCode === 400 &&
        (errorUpiMandateStatusData?.data?.responseCode === "UPIErr011" ||
          !errorUpiMandateStatusData?.data)
      ) {
        showErrorModal(errorUpiMandateStatusData);
        return;
      } else if (
        errStatusCode === 500 &&
        errorUpiMandateStatusData?.data?.responseCode === "UPIRES-000"
      ) {
        if (count <= MAX_MANDATE_COUNT) {
          // poll mandate Status
          return scheduleUpiMandateStatus();
        } else {
          showErrorModal(errorUpiMandateStatusData);
          refetchApplicationData();
          return;
        }
      } else if (errStatusCode === 500) {
        setOpenUpiModal(false);
        setErrorModalDetails({
          label: errorUpiMandateStatusData?.data?.message,
          subLabel:
            errorUpiMandateStatusData?.message ||
            errorUpiMandateStatusData?.data?.reason,
        });
        setModalVisible(true);
        setLoader(false);
        return;
      }
    }

    if (productApplication?.stage === STAGE?.E_SIGN) {
      router.push(ROUTES(product)?.E_SIGN_INSTRUCTIONS);
      return;
    } else if (productApplication?.stage === STAGE?.VKYC) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage)); //navigate to Esign/VKYC page
      return;
    } else if (upiMandateStatusData?.data?.stage === STAGE?.E_SIGN) {
      // setLoader(true);
      // setOpenUpiModal(false);
      refetchApplicationData();
    } else if (
      upiMandateStatusData?.data?.stage === STAGE?.MANDATE &&
      count <= MAX_MANDATE_COUNT
    ) {
      if (upiMandateStatusData?.data?.responseCode === "UPIRES-000") {
        return scheduleUpiMandateStatus();
      }
    } else if (
      upiMandateStatusData?.data?.stage === STAGE?.CREDIT_QUEUE &&
      count <= MAX_MANDATE_COUNT
    ) {
      return scheduleUpiMandateStatus();
    }
    if (count > MAX_MANDATE_COUNT) {
      setLoader(false);
      setOpenUpiModal(false);
      clearUpiTimer();
      if (upiMandateStatusData) {
        setErrorModalDetails({
          label: upiMandateStatusData?.data?.message,
          subLabel:
            upiMandateStatusData?.message || upiMandateStatusData?.data?.reason,
        });
      } else if (errorUpiMandateStatusData) {
        setErrorModalDetails({
          label: errorUpiMandateStatusData?.data?.message,
          subLabel:
            errorUpiMandateStatusData?.message ||
            errorUpiMandateStatusData?.data?.reason,
        });
      }

      handleMandateRetryCheck(upiMandateStatusData?.data?.data);
      setModalVisible(true);
      return;
    }
    if (errorUpiMandateStatusData) {
      // if does not satisfy any of the above conditions, show error modal
      showErrorModal(errorUpiMandateStatusData);
      return;
    }
  }, [
    upiTimerRef,
    upiMandateData,
    upiMandateStatusData,
    errorUpiMandateStatus,
    productApplication,
  ]);

  useEffect(() => {
    const errorDetail = errorCreateMandate?.data?.detail;
    const errorDetailMessage =
      typeof errorDetail === "string" ? errorDetail : errorDetail?.message;
    const errorMessageToShow =
      errorCreateMandate?.data?.message || errorDetailMessage;
    if (
      (errorCreateMandate && errorCreateMandate !== undefined) ||
      errorCreateMandate?.originalStatus === 503
    ) {
      // const mandateCountErrorMessage =
      //   errorCreateMandate?.data?.data?.mandateCountErrorMessage;

      if (errorCreateMandate?.status === 401) {
        SnackbarComponent({
          error: isError,
          errorMessage: "Your session has expired. Please login again",
        });
        handleLogout(); //navigate to login screen
        return;
      } else if (
        errorCreateMandate?.status === 400 &&
        // errorCreateMandate?.data?.data?.resCode === 'ykR20014'
        errorCreateMandate?.data?.data?.resCode === "ykR20008"
      ) {
        //this case only should happen in bank details where we put ifsc code
        // "responseDescripTion": "Invalid IFSC/MICR"
        setErrorModalDetails({
          modalErrorType: "exhaustTryWithNewNumber",
          // error: mandateCountErrorMessage,
        });
        handleMandateRetryCheck(errorCreateMandate?.data?.data);
        setModalVisible(true);
        return;
      } else if (
        errorCreateMandate?.status === 400 &&
        errorCreateMandate?.data?.data?.resCode === "ykE30004"
      ) {
        setErrorModalDetails({
          modalErrorType: "technicalIssue",
          // error: mandateCountErrorMessage,
        });
        handleMandateRetryCheck(errorCreateMandate?.data?.data);
        setModalVisible(true);
        return;
      } else if (
        errorCreateMandate?.data?.statusCode === 400 &&
        (errorCreateMandate?.data?.data?.reason?.toLowerCase() ===
          "name mismatch" ||
          errorCreateMandate?.data?.data?.message?.toLowerCase() ===
            "name mismatch" ||
          errorCreateMandate?.data?.data?.status?.toLowerCase() ===
            "name mismatch")
      ) {
        dispatch(
          setModalAttributes({
            open: true,
            message: `The bank account must belong to the person applying for the loan ${customerData?.data?.firstName} ${customerData?.data?.lastName}. In your case, it appears the account is registered under a different name. Please try again with your own bank details.`,
            type: SUBSTAGE?.KYC_BANK_NAME_MATCH_CREDIT_QUEUE,
          }),
        );
        return;
      } else if (
        errorCreateMandate?.data?.status === 400 ||
        errorCreateMandate?.data?.statusCode === 400
      ) {
        if (errorCreateMandate?.data?.data?.message) {
          dispatch(
            setModalAttributes({
              open: true,
              message: {
                label:
                  errorCreateMandate?.data?.message || "Something went wrong",
                subLabel: errorCreateMandate?.data?.data?.message,
              },
              type: "INVALID_ACCOUNT_DETAILS",
            }),
          );
        } else if (errorMessageToShow) {
          dispatch(
            setModalAttributes({
              open: true,
              message: {
                label: m("label"),
                subLabel: errorMessageToShow || "Please try again.",
              },
              type: "ATTEMPT_ERROR",
            }),
          );
        }
        return;
      } else if (
        errorCreateMandate?.status === 400 &&
        errorCreateMandate?.data?.data?.ResCode === "EmErr003"
      ) {
        setErrorModalDetails({
          modalErrorType: "mandateFailed",
          // error: mandateCountErrorMessage,
        });
        handleMandateRetryCheck(errorCreateMandate?.data?.data);
        setModalVisible(true);
        return;
      } else if (
        errorCreateMandate?.data?.status === 500 ||
        errorCreateMandate?.data?.status === 400
      ) {
        setErrorModalDetails({
          label: errorCreateMandate?.data?.message,
          // error: mandateCountErrorMessage,
          subLabel: "",
        });
        handleMandateRetryCheck(errorCreateMandate?.data?.data);
        setModalVisible(true);
        return;
      }
      return;
    }
    if (
      createMandateData &&
      createMandateData?.data &&
      createMandateData?.data?.ResCode === "EmRes001"
    ) {
      // navigate to payment gateway LinkOneMoney
      sessionStorageUtil?.set(
        APP_KEYS.MANDATE_ID,
        createMandateData?.data?.MdtID,
      );
      sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
      window.location.href = createMandateData?.data?.URL;
    }
    if (createMandateData?.data?.paymentLink) {
      // navigate to payment gateway LinkOneMoney
      sessionStorageUtil?.set(
        APP_KEYS.MANDATE_ID,
        createMandateData?.data?.MdtID,
      );
      sessionStorage.setItem(APP_KEYS.PRODUCT_TYPE, product);
      window.location.href = createMandateData?.data?.paymentLink;
    }
  }, [productApplication?.stage, createMandateData, errorCreateMandate]);

  useEffect(() => {
    setActions({
      onConfirm:
        productApplication?.subStage === SUBSTAGE?.MANDATE_POLLING
          ? null
          : formik.handleSubmit,
      disabledConfirmBtn:
        !formik.values.modeOfPayment ||
        productApplication?.subStage === SUBSTAGE?.MANDATE_POLLING,
    });
  }, [setActions, formik.isValid, formik.values, productApplication?.subStage]);
  useStageErrorHandler({
    stage: productApplication?.stage,
    prevStage: productApplication?.previousStage,
    message: productApplication?.loanReapplyMessage,
  });

  useEffect(() => {
    if (
      !mandateStatus &&
      orderStatus?.toLowerCase() === "charged" &&
      productApplication?.appId &&
      productApplication?.subStage === STAGE?.RPD_SUCCESS
    ) {
      upiMandate({
        appId: productApplication?.appId,
      });
    }
    if (mandateStatus?.toLowerCase() === "failure") {
      setErrorModalDetails({
        modalErrorType: "emandateFailed",
        onBtnClick: () => setModalVisible(false),
      });
      setModalVisible(true);
    }
  }, [
    mandateStatus,
    orderStatus,
    productApplication?.appId,
    productApplication?.subStage,
  ]);
  /***************************************ACTION************************************ */
  const handleFieldChange = (e) => {
    const nextMode = e.target.value;

    if (nextMode !== "upi") {
      formik.setFieldValue("upiId", "", false);
      formik.setFieldTouched("upiId", false, false);
      formik.setFieldError("upiId", undefined);
    }

    formik.setFieldValue("modeOfPayment", nextMode, true);
  };

  const findErrorCode = (responseCode) => {
    if (!responseCode) {
      return;
    }
    let errorData = resCodeErrorValidator(responseCode);
    setErrorModalDetails({
      subLabel: errorData?.description,
      modalErrorType: errorData?.name,
    });
    setModalVisible(true);
  };

  const onPressErrorButton = () => {
    setModalVisible(false);
    setCount(0);
    if (productApplication?.stage === STAGE?.CREDIT_QUEUE) {
      refetchApplicationData();
      router.push("/"); //naviagte to home beacuse we don't have home dashboard
    }
  };

  const isMatching =
    accountNumber &&
    productApplication?.aaCallBackAccountNumber &&
    productApplication?.aaCallBackAccountNumber.slice(-4) ===
      accountNumber.slice(-4) &&
    accountNumber.length ===
      productApplication?.aaCallBackAccountNumber?.length;

  /***************************************RENDER************************************ */
  return (
    <Stack spacing={2} mb={2}>
      <LoaderComponent open={isloading} />
      <PaperComponent
        paperStyle={{ backgroundColor: "rgba(242, 247, 247, 0.6)" }}
      >
        <Typography
          color={WEB_COLORS.primary}
          fontSize={{ xs: 20, sm: 24 }}
          fontWeight={700}
        >
          Mode of Repayment
        </Typography>
        <Typography color={WEB_COLORS.fadeColor}>
          {t("repaymentMode.tittle")}
        </Typography>
        <FormControl fullWidth>
          <RadioGroup
            defaultValue={""}
            name="modeOfPayment"
            onChange={(e) => {
              handleFieldChange(e);
            }}
          >
            <PaymentMethods
              appId={productApplication?.appId}
              formik={formik}
              openUpiInput={openUpiInput}
              verificationMode={productApplication?.bankVerificationMode}
              isRpl={isRPL}
            />
          </RadioGroup>
        </FormControl>
      </PaperComponent>
      <Stack direction={"row"} alignItems={"center"}>
        <IconButton
          sx={{
            color: "#FFC700",
          }}
        >
          <Info />
        </IconButton>
        <Typography color={WEB_COLORS.textFadeColor}>
          {Number(productApplication?.selectOfferEmi) < 15000
            ? t("repaymentMode.note2")
            : t("repaymentMode.note1")}
        </Typography>
      </Stack>

      <ErrorModal
        open={modalVisible}
        handleModal={setModalVisible}
        data={errorModalDetails}
        onPress={() => {
          onPressErrorButton();
        }}
      />
      <CurrentAccountWarningModal
        open={currentAccountModal}
        name={productApplication?.aadharFullName}
        onSuccess={handleCurrentAccountWarningSuccess}
      />

      <ModalComponent
        open={openUpiModal}
        handleModal={setOpenUpiModal}
        title={t("upimodal.approverequest")}
        maxWidth={isMobile ? "sm" : "md"}
        fullScreen={isMobile}
        closeButtonRequired={
          productApplication?.subStage ===
          SUBSTAGE.UPI_MANDATE_CALLBACK_RECIEVED_REJECTED
        }
      >
        <Typography
          color={"#727272"}
          mt={-3}
          fontWeight={400}
          fontSize={{ xs: 14, md: 16 }}
        >
          Follow these steps
        </Typography>
        <Stack
          sx={{
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            padding: "16px",
            mt: 2,
          }}
        >
          {MANDATE_SETUP_NOTIFICATION().map(
            ({ sNo, text, desc, image, isDivider }) => (
              <Box key={sNo}>
                <Stack direction={"row"} alignItems={"center"} gap={2}>
                  <Image width={44} height={44} src={image} alt="Images" />
                  <Stack>
                    <Typography
                      fontWeight={500}
                      fontSize={{ xs: 16, md: 20 }}
                      color={WEB_COLORS.primary}
                    >
                      {text}
                    </Typography>
                    <Typography
                      fontSize={{ md: 16, xs: 14 }}
                      fontWeight={400}
                      color={WEB_COLORS.textFadeColor}
                    >
                      {desc}
                    </Typography>
                  </Stack>
                </Stack>
                {isDivider && (
                  <Image
                    style={{ marginLeft: "20px" }}
                    src={IMAGES.stepdivider}
                    alt="Divider"
                  />
                )}
              </Box>
            ),
          )}
        </Stack>
        <Stack
          sx={{
            background: "rgba(254, 247, 219, 1)",
            border: "1px solid #E0E0E0",
            borderRadius: "8px",
            padding: "16px",
            marginTop: "16px",
          }}
        >
          <Stack direction={"row"} alignItems={"center"} gap={1}>
            <Image src={IMAGES.infoIcon} alt="Info" />
            <Typography fontWeight={"bold"}>{t("upimodal.note")}</Typography>
            <Typography>{t("upimodal.notedesc")}</Typography>
          </Stack>
        </Stack>
        <Stack mt={2}>
          <LoaderComponent open={loader} stage={STAGE.MANDATE} />
        </Stack>
      </ModalComponent>
      <ModalComponent
        open={openAccountModal}
        handleModal={setOpenAccountModal}
        title="Bank Account Verification"
      >
        <Stack gap={2}>
          <Typography
            sx={{ fontWeight: "medium", fontSize: { md: 16, xs: 14 } }}
          >
            Bank Name: {productApplication?.bankName}
          </Typography>
          <Typography
            sx={{ fontWeight: "medium", fontSize: { md: 16, xs: 14 } }}
          >
            Bank IFSC Code: {productApplication?.bankIFSCCode}
          </Typography>
          <Typography fontWeight={"bold"} color={WEB_COLORS.primary}>
            Bank Account Number: {productApplication?.bankAccountNumber}
          </Typography>
          <Typography>
            Please enter above bank account number to proceed
          </Typography>
          <TextFieldComponent
            label="Bank Account Number"
            value={accountNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 20);
              if (value.length === 0) {
                setConfirmAccountNumber("");
              }
              setAccountNumber(value);
            }}
            type="number"
            maxLength={20}
            error={!!accountNumber && accountNumber.length < 1}
            errorMessage={
              !!accountNumber &&
              accountNumber.length < 1 &&
              "Bank Account Number is required"
            }
          />
          <TextFieldComponent
            type="number"
            label="Confirm Bank Account Number"
            disabled={!accountNumber}
            value={confirmAccountNumber}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, "").slice(0, 20);
              setConfirmAccountNumber(value);
            }}
            onPaste={(e) => e.preventDefault()}
            maxLength={20}
            error={
              !!confirmAccountNumber && accountNumber !== confirmAccountNumber
            }
            errorMessage={
              !!confirmAccountNumber &&
              accountNumber !== confirmAccountNumber &&
              "Entered Account number does not match"
            }
            inputPropsStyle={{ WebkitTextSecurity: "disc" }}
          />
          <ButtonComponent
            disabled={
              !isMatching ||
              !accountNumber ||
              !confirmAccountNumber ||
              accountNumber !== confirmAccountNumber
            }
            onClick={() => {
              upiMandate({
                appId: productApplication?.appId,
                accountNumber: accountNumber,
                customerVpa: formik.values?.upiId,
              });
              setOpenAccountModal(false);
              setAccountNumber("");
              setConfirmAccountNumber("");
            }}
          >
            Verify
          </ButtonComponent>
        </Stack>
      </ModalComponent>
    </Stack>
  );
};

export default RepaymentModeScreen;
